<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    width="800px"
    class="instagram-scenario-preview-dialog"
  >
    <div slot="title">プレビュー</div>

    <div v-loading="store.loading" class="dialog-content">
      <div class="control-area">
        <div class="control-area__field">
          <div class="control-area__label-row">
            <label class="control-area__label">抽選パターン</label>
            <el-tooltip placement="top">
              <template v-slot:content>
                プレビューで確認する抽選結果を切り替えられます。<br />
                「未フォロー」はフォローチェックが有効なとき、「離脱」はフォローチェックが有効またはアンケートが設定されているときに表示されます。
              </template>
              <template v-slot:default>
                <i class="mdi mdi-help-circle control-area__label-help" />
              </template>
            </el-tooltip>
          </div>
          <el-select
            class="control-area__select control-area__select--pattern"
            :value="store.pattern"
            :disabled="isControlDisabled"
            @change="onPatternChange"
          >
            <el-option
              v-for="option in visiblePatternOptions"
              :key="option.value"
              :value="option.value"
              :label="option.label"
            />
          </el-select>
        </div>

        <div v-if="store.selectNodes.length > 0" class="control-area__answers">
          <div v-for="selectNode in store.selectNodes" :key="selectNode.id" class="control-area__field">
            <label class="control-area__label">{{ selectNode.text }}</label>
            <el-select
              class="control-area__select"
              :value="getAnswerValue(selectNode)"
              :disabled="isControlDisabled"
              @change="optionId => onAnswerChange(selectNode.id, optionId)"
            >
              <el-option
                v-for="option in selectNode.selectOption"
                :key="option.id"
                :value="option.id"
                :label="option.text"
              />
            </el-select>
          </div>
        </div>

        <div class="control-area__action-row">
          <div class="control-area__actions">
            <el-button
              v-if="store.playbackState === 'before'"
              class="control-area__action control-area__action--start"
              type="primary"
              @mousedown.native.prevent
              @click="store.restart()"
            >
              プレビュー開始
            </el-button>
            <el-button
              v-else-if="store.playbackState === 'playing'"
              class="control-area__action control-area__action--stop"
              type="primary"
              @mousedown.native.prevent
              @click="store.stop()"
            >
              プレビュー停止
            </el-button>
            <template v-else-if="store.playbackState === 'paused'">
              <el-button
                class="control-area__action control-area__action--resume"
                type="primary"
                @mousedown.native.prevent
                @click="store.play()"
              >
                プレビュー再開
              </el-button>
              <el-button
                class="control-area__action control-area__action--restart"
                @mousedown.native.prevent
                @click="store.restart()"
              >
                最初から確認
              </el-button>
            </template>
            <el-button
              v-else-if="store.playbackState === 'done'"
              class="control-area__action control-area__action--again"
              type="primary"
              @mousedown.native.prevent
              @click="store.restart()"
            >
              もう一度確認
            </el-button>
          </div>

          <div class="control-area__interval">
            <div class="control-area__interval-header">
              <span class="control-area__label">プレビューの速度間隔</span>
              <el-tooltip placement="right">
                <template v-slot:content>
                  プレビューの速度間隔(秒)を設定できます。<br />
                  「遅」に近づくほど、ゆっくり描画します。
                </template>
                <template v-slot:default>
                  <i class="mdi mdi-help-circle control-area__interval-help" />
                </template>
              </el-tooltip>
            </div>
            <div class="control-area__interval-slider">
              <span class="control-area__interval-edge">早</span>
              <el-slider
                :value="stepIntervalSec"
                :min="PREVIEW_STEP_INTERVAL_SEC_MIN"
                :max="PREVIEW_STEP_INTERVAL_SEC_MAX"
                :step="PREVIEW_STEP_INTERVAL_SEC_STEP"
                :disabled="isControlDisabled"
                :format-tooltip="formatStepIntervalTooltip"
                @input="onStepIntervalChange"
              />
              <span class="control-area__interval-edge">遅</span>
            </div>
          </div>
        </div>

        <Message type="info"> 確認したい抽選パターンを選択し、プレビュー開始をクリックしてください。 </Message>
      </div>

      <div v-if="store.account" class="preview-frame">
        <InDmFrame :picture-url="store.account.pictureUrl" :screen-name="store.account.screenName">
          <InDmChatArea
            :key="chatAreaKey"
            :account="store.account"
            :chat-items="store.chatItems"
            :playback-control="store.playbackControl"
            @update:playbackControl="store.syncPlaybackControl"
            @update:visibleCount="store.syncVisibleCount"
          />
        </InDmFrame>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'

import Message from '@/client/components/_atoms/Message.vue'
import InDmChatArea from '@/client/components/_organisms/in_dm_chat_area'
import InDmFrame from '@/client/components/_organisms/in_dm_frame'
import { useInstagramScenarioPreviewStore } from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioPreviewStore'
import type { PreviewPattern } from '@/client/features/instagram_instantwin_prize/utils/buildPreviewChatItems'
import {
  PREVIEW_STEP_INTERVAL_SEC_MAX,
  PREVIEW_STEP_INTERVAL_SEC_MIN,
  PREVIEW_STEP_INTERVAL_SEC_STEP
} from '@/client/features/instagram_instantwin_prize/utils/previewConstants'
import type { NodeResponse } from '@/typings/api/instagram_instantwin_node_container'

interface Props {
  visible: boolean
  prizeId: number | null
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const store = useInstagramScenarioPreviewStore()

const PATTERN_OPTIONS: { value: PreviewPattern; label: string }[] = [
  { value: 'win', label: '当選' },
  { value: 'lose', label: '落選' },
  { value: 'unfollow', label: '未フォロー' },
  { value: 'missing', label: '離脱' }
]

type PreviewSelectNode = Extract<NodeResponse, { type: 'SELECT' }>

const isControlDisabled = computed(() => store.playbackState === 'playing')

const visiblePatternOptions = computed(() =>
  PATTERN_OPTIONS.filter(option => {
    if (option.value === 'unfollow') return store.canShowUnfollow
    if (option.value === 'missing') return store.canShowMissing
    return true
  })
)

const chatAreaKey = computed(() => {
  const answerKey = Object.entries(store.answers)
    .map(([selectNodeId, optionId]) => `${selectNodeId}=${optionId}`)
    .join(',')
  return `${store.pattern}:${answerKey}`
})

function getAnswerValue(selectNode: PreviewSelectNode): number | null {
  return store.answers[selectNode.id] ?? selectNode.selectOption[0]?.id ?? null
}

function onPatternChange(next: PreviewPattern): void {
  store.setPattern(next)
}

function onAnswerChange(selectNodeId: number, optionId: number): void {
  store.setAnswer(selectNodeId, optionId)
}

const stepIntervalSec = computed(() => store.playbackControl.stepIntervalMs / 1000)

function onStepIntervalChange(nextSec: number): void {
  store.setStepIntervalMs(Math.round(nextSec * 1000))
}

function formatStepIntervalTooltip(sec: number): string {
  return `${sec.toFixed(1)} 秒`
}

watch(
  () => props.visible,
  async visible => {
    if (!visible) {
      store.close()
      return
    }
    if (props.prizeId === null) return
    await store.open(props.prizeId).catch(() => undefined)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  store.close()
})

function handleClose(): void {
  emit('close')
}
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-dialog) {
  .el-dialog__header {
    padding: $spacing-unit * 2;
    font-size: $font-large-size;
    color: $text-color-white !important;
    background-color: $background-dark-color;
  }

  .el-dialog__body {
    padding: $spacing-unit * 2;
  }

  .el-dialog__title,
  .el-dialog__headerbtn .el-dialog__close {
    color: $text-color-white;
  }
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit * 3;
  min-height: 200px;
}

.preview-frame {
  display: flex;
  justify-content: center;
}

.preview-frame :deep(.in-dm-frame) {
  zoom: 1;
}

.control-area {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit * 2;
}

.control-area__field {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
}

.control-area__label {
  font-size: $font-small-size;
  font-weight: $font-weight-bold;
}

.control-area__label-row {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.control-area__label-help {
  margin: 0;
  cursor: pointer;
}

.control-area__select {
  width: 100%;
}

.control-area__select--pattern {
  width: 152px;
}

.control-area__answers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacing-unit * 3;
}

.control-area__action-row {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit * 2;
  align-items: flex-start;
}

.control-area__actions {
  display: flex;
  flex-shrink: 0;
  gap: $spacing-unit * 3;
}

.control-area__interval {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 4px;
  width: 200px;
}

.control-area__interval-header {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.control-area__interval-help {
  margin: 0;
  cursor: pointer;
}

.control-area__interval-slider {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.control-area__interval-edge {
  flex-shrink: 0;
  width: 20px;
  font-size: $font-small-size;
  text-align: center;
}

.control-area__interval-slider :deep(.el-slider) {
  flex: 1;
}

.control-area__action--start,
.control-area__action--stop,
.control-area__action--resume {
  width: 132px;
}

.control-area__action--restart,
.control-area__action--again {
  width: 116px;
}

.control-area__action--restart {
  margin-left: 0;
}

.control-area__notice {
  padding: $spacing-unit $spacing-unit * 1.5;
  margin: 0;
  font-size: $font-small-size;
  color: $sub-text-color;
  background-color: $background-color;
  border-radius: $round;
}
</style>
